import { CustomerRequestInfo } from "Model/CustomerRequestInfo";
import { OrderInfo } from "Model/OrderInfo";
import Resizer from "react-image-file-resizer";
import USStates from "Components/USStates.json";

export const getStateAbbreviation = (stateName: string): string => {
  const state = USStates.find(
    (state) => state.name.toLowerCase() === stateName.toLowerCase()
  );
  return state ? state.abbreviation : stateName;
};

export const getStateName = (abbreviation: string): string => {
  const state = USStates.find(
    (state) => state.abbreviation.toLowerCase() === abbreviation.toLowerCase()
  );
  return state ? state.name : abbreviation;
};

// a little function to help us with reordering the result
export const reorder = <T>(
  list: T[],
  startIndex: number,
  endIndex: number
): T[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

// A little helper function to reorder a list
export const reorder2 = (
  list: any[],
  startIndex: number,
  endIndex: number
): any[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

export const resizeFile = (file: File, maxWidth: number, maxHeight: number) =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      maxWidth,
      maxHeight,
      "JPEG",
      85,
      0,
      (uri) => {
        resolve(uri);
      },
      "file"
    );
  });

/**
 * Resizes image first, then compresses to approximately 300KB or less
 * @param file - The image file to compress
 * @param maxWidth - Maximum width (default: 1200)
 * @param maxHeight - Maximum height (default: 1200)
 * @returns Promise<File> - The resized and compressed image file
 */
export const compressImage = async (
  file: File,
  maxWidth: number = 1200,
  maxHeight: number = 1200
): Promise<File> => {
  const targetSize = 300 * 1024; // 300KB in bytes

  // Step 1: Resize image to target dimensions first using resizeFile
  const resizedFile = (await resizeFile(file, maxWidth, maxHeight)) as File;

  // Step 2: If resized file is already under 300KB, return it
  if (resizedFile.size <= targetSize) {
    return resizedFile;
  }

  // Step 3: Compress by reducing quality iteratively (JPEG format)
  let quality = 80;
  let compressedFile = await new Promise<File>((resolve) => {
    Resizer.imageFileResizer(
      file,
      maxWidth,
      maxHeight,
      "JPEG",
      quality,
      0,
      (uri) => {
        resolve(uri as File);
      },
      "file"
    );
  });

  let iterations = 0;
  const maxIterations = 6;

  while (
    compressedFile.size > targetSize &&
    quality > 30 &&
    iterations < maxIterations
  ) {
    quality -= 10;
    compressedFile = await new Promise<File>((resolve) => {
      Resizer.imageFileResizer(
        file,
        maxWidth,
        maxHeight,
        "JPEG",
        quality,
        0,
        (uri) => {
          resolve(uri as File);
        },
        "file"
      );
    });
    iterations++;
  }

  // Step 4: If still too large after quality reduction, reduce dimensions
  if (compressedFile.size > targetSize) {
    const scaleFactor = 0.8;
    compressedFile = await new Promise<File>((resolve) => {
      Resizer.imageFileResizer(
        file,
        Math.floor(maxWidth * scaleFactor),
        Math.floor(maxHeight * scaleFactor),
        "JPEG",
        quality,
        0,
        (uri) => {
          resolve(uri as File);
        },
        "file"
      );
    });
  }

  return compressedFile;
};

export const convertRequestToOrder = (
  formId: string,
  requestInfo: CustomerRequestInfo
): OrderInfo => {
  const o: OrderInfo = {
    formId: formId,
    requestId: requestInfo.requestId,
    orderCreated: "",
    requestCreated: requestInfo.created,
    orderTotal: requestInfo.orderTotal,
    paid: 0,
    orderId: "",
    status: "",
    identifier: "",
    notes: "",
    firstName: requestInfo.firstName,
    lastName: requestInfo.lastName,
    email: requestInfo.email,
    phoneNumber: requestInfo.phoneNumber,
    preferredContact: requestInfo.preferredContact,
    customerDate: requestInfo.customerDate,
    customerFulfillment: requestInfo.customerFulfillment,
    shippingAddress1: requestInfo.shippingAddress1,
    shippingAddress2: requestInfo.shippingAddress2,
    shippingCity: requestInfo.shippingCity,
    shippingState: requestInfo.shippingState,
    shippingZip: requestInfo.shippingZip,
    event: requestInfo.event,
    theme: requestInfo.theme,
    colors: requestInfo.colors,
    personalization: requestInfo.personalization,
    count: requestInfo.count,
    questions: requestInfo.questions,
    paymentChecked: requestInfo.paymentChecked,
    bucketLists: requestInfo.bucketLists,
    flavors: requestInfo.flavors,
    packagings: requestInfo.packagings,
    designInspirations: requestInfo.designInspirations,
    unit: requestInfo.unit,
    updated: requestInfo.updated,
    created: requestInfo.created,
  };
  return o;
};

/**
 * Detects if the user is browsing from Facebook or Instagram in-app browser
 * @returns boolean - true if in-app browser is detected
 */
export const isInAppBrowser = (): boolean => {
  const userAgent = navigator.userAgent || navigator.vendor || "";

  return (
    userAgent.includes("Instagram") ||
    userAgent.includes("FBAN") ||
    userAgent.includes("FBAV") ||
    userAgent.includes("FB_IAB") ||
    userAgent.includes("FB4A")
  );
};
